import React, { useEffect, useState } from 'react';
import { useDirtyTracker } from "../../../../../util/useDirtyTracker";
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../../../auth/useAuth0';
import { useBusinessContext } from '../../../../../context/BusinessContext';
import { useAlert } from '../../../../../context/alert/AlertContext';
import { formatPhone, isPhoneComplete, resolvePhoneCountry } from '../../../../../util/phone';
import { BusinessDetails } from '../../../../../model/business/BusinessDetails';
import { BusinessDetailsInput } from '../../../../../service/business/BusinessDetailsInput';
import { findBusinessDetailsByBusinessId, saveBusinessDetails } from '../../../../../service/business/business-service';
import { mapSourceToTarget } from '../../../../../util/util';
import ActionsButtonsComponent from '../../../../commons/action-buttons/ActionsButtonsComponent';
import { uploadImage, mediaUrl } from '../../../../../service/media/media-service';
import { normalizeInstagram, normalizeFacebook } from '../../../../../util/links';
import { validateImageFile } from '../../../../../util/imageUpload';

/**
 * Settings → Business → Public Profile.
 *
 * Edits the marketplace-facing fields (cover photo, category, about, socials…)
 * that the consumer site shows on the business page. Loads the FULL business
 * details and saves them back via saveDetails, so the other detail fields are
 * preserved untouched.
 */
const BusinessPublicProfileComponent: React.FC = () => {
    const { t } = useTranslation();
    const { getAccessTokenSilently, logout } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();

    const [detailsData, setDetailsData] = useState<BusinessDetails | null>(null);
    // QA §5.10: dirty DERIVADO de baseline (não boolean por onChange).
    const { isModified, setBaseline } = useDirtyTracker(detailsData);
    const [socialErr, setSocialErr] = useState<{ instagram?: boolean; facebook?: boolean }>({});
    // Cover chosen but not yet uploaded — held until Save (D16).
    const [pendingCover, setPendingCover] = useState<File | null>(null);
    const [pendingCoverPreview, setPendingCoverPreview] = useState<string | null>(null);
    // A deferred cover is an unsaved change even though it isn't in detailsData yet.
    const dirty = isModified || pendingCover != null;

    const fetchDetails = React.useCallback(async () => {
        try {
            const token = await getAccessTokenSilently();
            if (!selectedBusiness?.id) {
                setAlert('danger', t('settings.business.alerts.publicProfileNotLoaded'));
                return;
            }
            const fetched = await findBusinessDetailsByBusinessId(selectedBusiness.id, token);
            if (fetched) {
                if (selectedBusiness.id !== fetched.id) await logout();
                setBaseline(fetched);
                setDetailsData(fetched);
            }
        } catch (error) {
            const msg = error instanceof Error ? error.message : 'Unknown error';
            setAlert('danger', t('settings.business.alerts.publicProfileLoadError', { error: msg }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getAccessTokenSilently, logout, selectedBusiness, setAlert, setBaseline]);

    useEffect(() => {
        fetchDetails();
    }, [fetchDetails]);

    const handleChange = (field: keyof BusinessDetails, value: any) => {
        setDetailsData((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

    const handleCoverUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];
        if (!selectedBusiness?.id) { setAlert('danger', t('settings.business.alerts.selectBeforeImage')); e.target.value = ''; return; }
        // Validate type + size before anything (D6/D14).
        const check = validateImageFile(file);
        if (!check.ok) {
            setAlert('danger', t(check.error === 'size'
                ? 'settings.business.alerts.imageTooLarge'
                : 'settings.business.alerts.imageBadType'));
            e.target.value = '';
            return;
        }
        // Defer the R2 upload to Save (D16) — hold the File + a local preview.
        setPendingCover(file);
        setPendingCoverPreview((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(file);
        });
        // Reset so re-selecting the SAME file fires onChange again.
        e.target.value = '';
    };

    const handleSave = async () => {
        if (!detailsData) return;
        if (selectedBusiness?.id !== detailsData.id) { await logout(); return; }
        const phoneCountry = resolvePhoneCountry(detailsData.country, selectedBusiness?.currency);
        if (!isPhoneComplete(detailsData.publicPhone ?? '', phoneCountry)) {
            setAlert('danger', t('common.invalidPhone'));
            return;
        }
        // Normalize + validate social links (empty is fine).
        const ig = normalizeInstagram(detailsData.instagram);
        const fb = normalizeFacebook(detailsData.facebook);
        if (!ig.ok || !fb.ok) {
            setSocialErr({ instagram: !ig.ok, facebook: !fb.ok });
            setAlert('danger', t('settings.business.publicProfile.socialInvalid'));
            return;
        }
        // Trim every free-text field so no leading/trailing spaces reach the public page.
        const tr = (v?: string | null) => (v ?? '').trim() || null;
        try {
            const token = await getAccessTokenSilently();
            // Upload the deferred cover now (D16), only on Save. Abort on failure.
            let coverKey = detailsData.coverImage;
            if (pendingCover) {
                try {
                    coverKey = await uploadImage(pendingCover, `business/${detailsData.id}`, token);
                } catch (err) {
                    setAlert('danger', err instanceof Error ? err.message : t('settings.business.publicProfile.coverUploadError'));
                    return;
                }
            }
            const cleaned = {
                ...detailsData,
                coverImage: coverKey,
                headline: tr(detailsData.headline), about: tr(detailsData.about),
                highlight: tr(detailsData.highlight), category: tr(detailsData.category),
                instagram: ig.value, facebook: fb.value,
            };
            const input: BusinessDetailsInput = mapSourceToTarget(cleaned, BusinessDetailsInput);
            const result = await saveBusinessDetails(input, token);
            if (result.success) {
                setDetailsData(cleaned);
                setBaseline(cleaned); // o salvo vira o novo baseline limpo
                if (pendingCoverPreview) URL.revokeObjectURL(pendingCoverPreview);
                setPendingCover(null);
                setPendingCoverPreview(null);
                setAlert('success', t('settings.business.alerts.publicProfileSaved'));
            } else {
                setAlert('danger', t('settings.business.alerts.publicProfileSaveError', { error: result.error }));
            }
        } catch (error) {
            const msg = error instanceof Error ? error.message : 'Unknown error';
            setAlert('danger', t('settings.business.alerts.publicProfileSaveErrorGeneric', { error: msg }));
        }
    };

    // Descartar restaura o estado ORIGINAL (recarrega do backend), igual às
    // seções Calendário/Notificações — antes só limpava a flag e as edições
    // continuavam na tela.
    const handleCancel = () => {
        if (pendingCoverPreview) URL.revokeObjectURL(pendingCoverPreview);
        setPendingCover(null);
        setPendingCoverPreview(null);
        fetchDetails(); // recarrega + reseta o baseline → dirty volta a false
    };

    return (
        <div className="side-content-vertical">
            <ActionsButtonsComponent isModified={dirty} onSave={handleSave} onCancel={handleCancel} />

            <div className="profile-section-container separated-container">
                <h3 className="section-header">{t('settings.business.publicProfile.sectionTitle')}</h3>
                <p className="form-text" style={{ marginTop: '-0.5rem', marginBottom: '1rem' }}>
                    {t('settings.business.publicProfile.sectionHint')}
                </p>
                <form className="busniness-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="category">{t('settings.business.publicProfile.category')}</label>
                            <input
                                type="text"
                                id="category"
                                data-cy="biz-profile-category-input"
                                maxLength={80}
                                value={detailsData?.category ?? ''}
                                onChange={(e) => handleChange('category', e.target.value)}
                                placeholder={t('settings.business.publicProfile.categoryPlaceholder')}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="yearsExperience">{t('settings.business.publicProfile.yearsExperience')}</label>
                            <input
                                type="text" inputMode="numeric" maxLength={2}
                                id="yearsExperience"
                                data-cy="biz-profile-years-experience-input"
                                min={0}
                                value={detailsData?.yearsExperience ?? ''}
                                onChange={(e) =>
                                    handleChange('yearsExperience', e.target.value.replace(/\D/g, '') === '' ? null : Number(e.target.value.replace(/\D/g, '')))
                                }
                                placeholder="0"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group" style={{ flexBasis: '100%' }}>
                            <label htmlFor="headline">{t('settings.business.publicProfile.headline')}</label>
                            <input
                                type="text"
                                id="headline"
                                data-cy="biz-profile-headline-input"
                                maxLength={160}
                                value={detailsData?.headline ?? ''}
                                onChange={(e) => handleChange('headline', e.target.value)}
                                placeholder={t('settings.business.publicProfile.headlinePlaceholder')}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group" style={{ flexBasis: '100%' }}>
                            <label htmlFor="about">{t('settings.business.publicProfile.about')}</label>
                            <textarea
                                id="about"
                                data-cy="biz-profile-about-textarea"
                                maxLength={1000}
                                rows={4}
                                value={detailsData?.about ?? ''}
                                onChange={(e) => handleChange('about', e.target.value)}
                                placeholder={t('settings.business.publicProfile.aboutPlaceholder')}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group" style={{ flexBasis: '100%' }}>
                            <label htmlFor="highlight">{t('settings.business.publicProfile.highlight')}</label>
                            <input
                                type="text"
                                id="highlight"
                                data-cy="biz-profile-highlight-input"
                                maxLength={200}
                                value={detailsData?.highlight ?? ''}
                                onChange={(e) => handleChange('highlight', e.target.value)}
                                placeholder={t('settings.business.publicProfile.highlightPlaceholder')}
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group" style={{ flexBasis: '100%' }}>
                            <label htmlFor="coverImage">{t('settings.business.publicProfile.coverPhoto')}</label>
                            {/* External-URL field removed (D6): a cover hosted off-site
                               is mixed-content + can be swapped out later. Upload only. */}
                            <input
                                type="file"
                                id="coverImage"
                                accept="image/png,image/jpeg,image/webp,image/gif"
                                data-cy="biz-profile-cover-image-input"
                                onChange={handleCoverUpload}
                                style={{ marginBottom: '0.5rem' }}
                            />
                            {(pendingCoverPreview || detailsData?.coverImage) ? (
                                // eslint-disable-next-line jsx-a11y/img-redundant-alt
                                <img
                                    src={pendingCoverPreview || mediaUrl(detailsData?.coverImage)}
                                    alt="Cover preview"
                                    style={{
                                        marginTop: '0.6rem', width: '100%', maxHeight: 160,
                                        objectFit: 'cover', borderRadius: 12, border: '1px solid #ececf4',
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </form>
            </div>

            <div className="business-section-container separated-container">
                <h3 className="section-header">{t('settings.business.publicProfile.contactSocial')}</h3>
                <form className="busniness-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="publicPhone">{t('settings.business.publicProfile.publicPhone')}</label>
                            <input
                                type="text"
                                id="publicPhone"
                                data-cy="biz-profile-public-phone-input"
                                value={detailsData?.publicPhone ?? ''}
                                maxLength={30}
                                onChange={(e) => {
                                    const country = resolvePhoneCountry(detailsData?.country, selectedBusiness?.currency);
                                    handleChange('publicPhone', formatPhone(e.target.value, country));
                                }}
                                placeholder="(00) 00000-0000"
                            />
                            {!isPhoneComplete(detailsData?.publicPhone ?? '', resolvePhoneCountry(detailsData?.country, selectedBusiness?.currency)) &&
                             (detailsData?.publicPhone ?? '') !== '' && (
                                <span className="form-text text-danger small">{t('common.invalidPhone')}</span>
                            )}
                        </div>
                        <div className="form-group">
                            <label htmlFor="instagram">{t('settings.business.publicProfile.instagram')}</label>
                            <input
                                type="text"
                                id="instagram" maxLength={120}
                                data-cy="biz-profile-instagram-input"
                                value={detailsData?.instagram ?? ''}
                                onChange={(e) => { handleChange('instagram', e.target.value); setSocialErr((p) => ({ ...p, instagram: false })); }}
                                onBlur={(e) => { const r = normalizeInstagram(e.target.value); if (r.ok) { setSocialErr((p) => ({ ...p, instagram: false })); if (r.value !== (detailsData?.instagram ?? '')) handleChange('instagram', r.value ?? ''); } else setSocialErr((p) => ({ ...p, instagram: true })); }}
                                placeholder="@seunegocio ou instagram.com/seunegocio"
                            />
                            {socialErr.instagram && <small className="form-text text-danger" data-cy="biz-instagram-error">{t('settings.business.publicProfile.socialInvalid')}</small>}
                        </div>
                        <div className="form-group">
                            <label htmlFor="facebook">{t('settings.business.publicProfile.facebook')}</label>
                            <input
                                type="text"
                                id="facebook" maxLength={120}
                                data-cy="biz-profile-facebook-input"
                                value={detailsData?.facebook ?? ''}
                                onChange={(e) => { handleChange('facebook', e.target.value); setSocialErr((p) => ({ ...p, facebook: false })); }}
                                onBlur={(e) => { const r = normalizeFacebook(e.target.value); if (r.ok) { setSocialErr((p) => ({ ...p, facebook: false })); if (r.value !== (detailsData?.facebook ?? '')) handleChange('facebook', r.value ?? ''); } else setSocialErr((p) => ({ ...p, facebook: true })); }}
                                placeholder="@seunegocio ou facebook.com/seunegocio"
                            />
                            {socialErr.facebook && <small className="form-text text-danger" data-cy="biz-facebook-error">{t('settings.business.publicProfile.socialInvalid')}</small>}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BusinessPublicProfileComponent;
